import isEqual from 'lodash-es/isEqual';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FieldsetCard } from '@/components/atoms/FieldsetCard';
import { SectionLayout } from '@/components/atoms/SectionLayout/SectionLayout';
import { Form } from '@/components/ui/form';
import useBrains from '@/hooks/useBrains';
import useDialogs from '@/hooks/useDialogs';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { getPermissions } from '@/redux/permissions/selectors';
import { Brain, BrainReminder } from '@/shared/types/brain';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { convertMinutesToSeconds } from '@/util/dateUtils';
import { createYupResolver } from '@/util/forms/createYupResolver';
import { CLEAR_DIRTY_VALUES, KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';

import {
  FormReminder,
  isEmptyReminder,
  PreferencesForm,
  preferencesFormSchema,
} from './preferencesForm';
import { DEFAULT_TIMER_VALUES, ReminderMessageField } from './ReminderMessageField';
import { StartingDialogField } from './StartingDialogField';

const FORM_ID = 'preferences-form';

// Older reminders store minutes instead of seconds
const reminderSeconds = (reminder: BrainReminder) =>
  reminder.reminder_seconds ?? convertMinutesToSeconds(reminder.minutes ?? 0);

const bySeconds = (a: BrainReminder, b: BrainReminder) => a.reminder_seconds - b.reminder_seconds;

const toBrainReminders = (reminders: FormReminder[]): BrainReminder[] =>
  reminders
    .map((reminder) => ({
      reminder_id: reminder.reminder_id,
      reminder_seconds: +reminder.reminder_seconds,
      node_id: reminder.node.value,
    }))
    .sort(bySeconds);

interface PreferencesFieldsetProps {
  brain: Brain;
  brainId: string;
}

export const PreferencesFieldset = ({ brain, brainId }: PreferencesFieldsetProps) => {
  const { t } = useTranslation();
  const { detailedOptions } = useDialogs(brainId);
  const { updateBrainAsync } = useBrains(brainId);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  const guidelines = brain.guidelines ?? {};
  const reminders = guidelines.reminders ?? [];

  const formReminders = reminders.map((reminder) => ({
    reminder_id: reminder.reminder_id,
    reminder_seconds: reminderSeconds(reminder),
    node: detailedOptions.find((option) => option.value === reminder.node_id) ?? null,
  }));

  // Validate on save only, so a row the user is still filling in shows no errors
  const form = useForm<PreferencesForm>({
    mode: 'onSubmit',
    resolver: createYupResolver<PreferencesForm>(preferencesFormSchema),
    resetOptions: KEEP_DIRTY_VALUES,
    values: {
      starting_node_id: brain.starting_node_id ?? '',
      reminders: reminders.length === 0 ? [DEFAULT_TIMER_VALUES] : formReminders,
    },
  });

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = form;
  useTrackFormState({ isDirty, formId: FORM_ID });

  const onSubmit = async (data: PreferencesForm) => {
    // Drop rows the user added but never filled in, keeping one empty row as the empty state
    const filled = data.reminders.filter((reminder) => !isEmptyReminder(reminder));
    const cleaned = { ...data, reminders: filled.length > 0 ? filled : [DEFAULT_TIMER_VALUES] };
    const nextReminders = toBrainReminders(filled);
    const savedReminders = reminders
      .map((reminder) => ({
        reminder_id: reminder.reminder_id,
        reminder_seconds: reminderSeconds(reminder),
        node_id: reminder.node_id,
      }))
      .sort(bySeconds);

    // Only empty rows were added: removing them is the whole change
    const unchanged =
      (data.starting_node_id || null) === (brain.starting_node_id ?? null) &&
      isEqual(nextReminders, savedReminders);
    if (!unchanged) {
      await updateBrainAsync({
        brain_id: brainId,
        starting_node_id: data.starting_node_id || null,
        guidelines: { ...guidelines, reminders: nextReminders },
      });
    }
    form.reset(cleaned, CLEAR_DIRTY_VALUES);
  };

  return (
    // id anchors the canvas badge deeplink to the starting dialog
    <div id="conversation-start">
      <SectionLayout title={t('ai_agents.preferences.title')}>
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
            <FieldsetCard
              isDirty={isDirty}
              isLoading={isSubmitting}
              disabled={!isDirty || !canWrite}
              saveButtonLabel={t('common.save')}
              formId={FORM_ID}
            >
              <div className="flex flex-col gap-6">
                <StartingDialogField detailedOptions={detailedOptions} canWrite={canWrite} />
                <ReminderMessageField brain={brain} brainId={brainId} canWrite={canWrite} />
              </div>
            </FieldsetCard>
          </form>
        </Form>
      </SectionLayout>
    </div>
  );
};
